import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Button from '../../../composants/Button/Button';
import ValidationPopUp from '../../../composants/ValidationPopUp/ValidationPopUp';
import CGVAccept from '../../../composants/CGVAccept/CGVAccept';
import Banner from '../../../composants/Banner/Banner';
import SectionPliable from '../../../composants/SectionPliable/SectionPliable';
import OperationPreparationBC from '../../SuiviOperationBCV2/components/OperationPreparationBC';
import ModalArticles from '../../../composants/ModalArticles/ModalArticles';
import CataloguePreview from '../../../composants/CataloguePreview/CataloguePreview';
import { PrintButton } from '../../../composants/PrintButton/PrintButton';
import SignatureFlow from '../../../composants/Marche/SignatureFlow';
import NextTabButton from '../../../composants/NextTabButton/NextTabButton';
import AttestationDocuments from '../../../composants/AttestationDocuments/AttestationDocuments';
import ArticlesService, { Article } from '../../../services/articlesService';
import { formatPrice, getStatusColor } from '../../../utils/entityUtils';
import { useLotImage } from '../../../utils/getLotImage';
import { CommandeBC } from '../types';
import {
  formatCommandeArticles,
  totalArticlesHT,
  isCommandeExpired,
  prestataireHasActed,
  canAccepterRefuser,
  canPrestataireEditCatalogue,
  canPrestataireEditOffreTechnique,
  computePrestataireStatusBanner,
  computeOffreState,
  computeOffreStatusFlags,
  messageRefusPrestataire,
} from '../domain/commandeOffre';
import { computeOffreNextTabCta } from '../domain/commandeNextTab';
import {
  refuserCommande,
  deleteCatalogueArticle,
  saveOffreTechnique,
  saveAttestationDocuments,
  buildAcceptSignerUrl,
  buildTransferSignerUrl,
} from '../services/commandeBcService';
// Feuille generique partagee (memes imports que la case acheteur OperationOffre.css) : porte
// .articles-table-wrapper / .article-name / .total-section / .add-article-button etc. -> rendu
// identique. CommandeOffre.css ne garde que les classes presta sans equivalent acheteur.
import '../../../composants/ConsultationBC/ConsultationBC.css';
// Porte les classes .bcc-desc* (grille 12-col + champs read-only) partagees avec l'onglet
// Consultation du MEME module BC : le Descriptif Technique de l'Offre est un miroir de celui de
// la Consultation. Import intra-module (BC), aucun lien vers MS -> isolation MS/BC preservee.
import './CommandeConsultation.css';
import './CommandeOffre.css';

// Limite du champ libre "Offre technique" : alignee sur la validation back (max:5000).
const OFFRE_TECHNIQUE_MAX = 5000;

interface CommandeOffreProps {
  commande: CommandeBC;
  // Rafraichit la commande apres une action (remplace window.location.reload()).
  refetch: () => Promise<void> | void;
  // CGV de session, remontees au shell (parite OperationOffre acheteur l.41-42).
  acceptCGV?: boolean;
  setAcceptCGV?: React.Dispatch<React.SetStateAction<boolean>>;
  // Bascule vers "Notification et signature". Absente quand l'onglet n'est pas atteignable :
  // le CTA se masque alors de lui-meme (cf. computeOffreNextTabCta).
  onGoToNotification?: () => void;
  // Destination declaree apres signature (liste de preferences), arbitree sur l'etat frais.
  requestTab?: (preferences: string[]) => void;
}

// Type local du flux de signature en cours (accept | transfer). operationId === commandeId
// === commande.id cote presta : on ne porte que le mode + le signerUrl + les libelles succes.
type PendingSign = {
  mode: 'accept' | 'transfer';
  signerUrl: string;
  successTitle: string;
  successBody: string;
};

// Onglet OFFRE PRESTATAIRE-BC (V2) : le coeur de la valeur presta. Le presta y decide sur le
// bon de commande puis signe / envoie son offre.
//   - Panier NORMAL  : accepter (CGV -> signature .../accept) ou refuser (POST refuse, non signe).
//   - Panier INVERSE : chiffrer le catalogue (ModalArticles) puis envoyer (signature .../transfer).
//   - Etat Valide(7) : telecharger le bon de commande signe.
//   - Etats terminaux (8/9/14) : messages + bandeau ; AttenteAnalyse(13) : read-only + bandeau.
// Autonome : aucun import role/type (pas OffreBC / isClientView / useBCActions / bcService).
// id = commande.id (jamais window.location), gating 100% id-based, refetch via prop, signature
// via SignatureFlow (resourcePrefix='bdc/prestataire'), ValidationPopUp + inline (jamais alert).
const CommandeOffre: React.FC<CommandeOffreProps> = ({
  commande,
  refetch,
  acceptCGV = false,
  setAcceptCGV = () => {},
  onGoToNotification,
  requestTab,
}) => {
  const printRef = useRef<HTMLDivElement>(null);
  const lotImage = useLotImage(commande.operation?.lot_technique?.nom);

  // L'expiration peut survenir en direct : le chrono partage emet 'chronoExpired' a zero
  // (parite CommandeConsultation l.49-54).
  const [chronoExpired, setChronoExpired] = useState(false);
  useEffect(() => {
    const handler = () => setChronoExpired(true);
    window.addEventListener('chronoExpired', handler);
    return () => window.removeEventListener('chronoExpired', handler);
  }, []);

  // Articles existants (panier inverse) : seed depuis commande.articles, maj via ModalArticles
  // (onAddToCommand) et apres delete (legacy OffreBC useEffect l.270-332 + handleAddToCommand
  // l.1108-1115 + handleRemoveExistingArticle l.1118-1181).
  const seededArticles = useMemo(() => formatCommandeArticles(commande), [commande]);
  const [existingArticles, setExistingArticles] = useState<Article[]>(seededArticles);
  useEffect(() => {
    setExistingArticles(seededArticles);
  }, [seededArticles]);

  // Offre technique (texte libre presta) : seed depuis la commande, re-seed si la commande change
  // (parite existingArticles). lastSavedOffreRef memorise la derniere valeur persistee pour n'emettre
  // un PUT que si le texte a change (evite un aller-retour reseau inutile). offreTechniqueError porte
  // l'erreur d'obligation posee SUR le champ (regle 3), effacee des la premiere frappe.
  const [offreTechnique, setOffreTechnique] = useState<string>(commande.offre_technique ?? '');
  const [offreTechniqueError, setOffreTechniqueError] = useState<string | null>(null);
  const lastSavedOffreRef = useRef<string>(commande.offre_technique ?? '');
  useEffect(() => {
    const seed = commande.offre_technique ?? '';
    setOffreTechnique(seed);
    lastSavedOffreRef.current = seed;
  }, [commande.offre_technique]);

  // Attestation "documents administratifs a jour" (case OBLIGATOIRE avant envoi/acceptation, regle 4).
  // Etat initial = reflet de attestation_documents_at : cochee si deja attestee (regle 6). Re-seed si la
  // commande change (parite offreTechnique). attestationError porte l'erreur d'obligation posee SOUS la
  // case, effacee des la coche. Dispositif VOLONTAIREMENT DISTINCT des CGV (regle 7).
  const [attesteDocuments, setAttesteDocuments] = useState<boolean>(
    !!commande.attestation_documents_at,
  );
  const [attestationError, setAttestationError] = useState<string | null>(null);
  useEffect(() => {
    setAttesteDocuments(!!commande.attestation_documents_at);
  }, [commande.attestation_documents_at]);

  // Etats UI.
  const [isArticlesModalOpen, setIsArticlesModalOpen] = useState(false);
  const [isRefuseModalOpen, setIsRefuseModalOpen] = useState(false);
  const [isRefusing, setIsRefusing] = useState(false);
  const [refuseSuccess, setRefuseSuccess] = useState(false);
  const [isEnvoyerModalOpen, setIsEnvoyerModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  const [deletingIds, setDeletingIds] = useState<Set<string>>(new Set());
  const [inlineError, setInlineError] = useState<string | null>(null);

  // CGV avant signature ACCEPT uniquement (legacy handleAccepterAvecCGV l.518-535 ; la branche
  // ENVOYER OFFRE l.1184-1237 NE passe PAS par CGV).
  const [isCGVModalOpen, setIsCGVModalOpen] = useState(false);
  const [readyToAcceptAfterCGV, setReadyToAcceptAfterCGV] = useState(false);

  // Flux signature en cours (accept | transfer).
  const [pendingSign, setPendingSign] = useState<PendingSign | null>(null);

  // --- Derivations PURES (domaine) -------------------------------------------
  const expired = useMemo(() => isCommandeExpired(commande, chronoExpired), [commande, chronoExpired]);
  const hasActed = useMemo(() => prestataireHasActed(commande), [commande]);
  const readOnlyArticles = useMemo(() => formatCommandeArticles(commande), [commande]);
  const totalReadOnly = useMemo(() => totalArticlesHT(readOnlyArticles), [readOnlyArticles]);
  const totalChiffrage = useMemo(() => totalArticlesHT(existingArticles), [existingArticles]);
  const offreState = useMemo(() => computeOffreState(commande, chronoExpired), [commande, chronoExpired]);
  const statusFlags = useMemo(() => computeOffreStatusFlags(commande, chronoExpired), [commande, chronoExpired]);
  const statusBanner = useMemo(
    () => computePrestataireStatusBanner(commande, chronoExpired),
    [commande, chronoExpired],
  );
  const showAccepterRefuser = useMemo(
    () => canAccepterRefuser(commande, chronoExpired),
    [commande, chronoExpired],
  );
  const isChiffrage = useMemo(
    () => canPrestataireEditCatalogue(commande, chronoExpired),
    [commande, chronoExpired],
  );
  // Le champ "Offre technique" est-il encore editable (avant envoi) ou fige (des l'envoi) ?
  const canEditOffreTechnique = useMemo(
    () => canPrestataireEditOffreTechnique(commande, chronoExpired),
    [commande, chronoExpired],
  );

  // La case d'attestation n'a de sens que lorsqu'une offre est en jeu : soit le presta peut encore
  // agir (panier normal a accepter/refuser OU panier inverse a chiffrer), soit il vient d'envoyer et
  // attend l'analyse (regle 6 : la case reste affichee et interactive apres l'envoi). Dans les etats
  // terminaux (accepte/refuse/expire/annule), l'attestation est sans objet.
  const showAttestation = useMemo(
    () => showAccepterRefuser || isChiffrage || statusFlags.isWaiting,
    [showAccepterRefuser, isChiffrage, statusFlags.isWaiting],
  );

  // Sauvegarde du brouillon d'offre technique, a la perte du focus. UN PUT seulement si le texte a
  // change (lastSavedOffreRef) : sans ca, chaque blur sans edition ferait un aller-retour reseau.
  const handleSaveOffreTechnique = useCallback(async () => {
    if (offreTechnique === lastSavedOffreRef.current) return;
    try {
      await saveOffreTechnique(commande.id, offreTechnique);
      lastSavedOffreRef.current = offreTechnique;
    } catch (error) {
      setOffreTechniqueError(
        error instanceof Error ? error.message : "Erreur lors de l'enregistrement de l'offre technique.",
      );
    }
  }, [offreTechnique, commande.id]);

  // Garde d'obligation A L'ENVOI (miroir front de la garde back). Le champ est facultatif tant qu'on
  // enregistre un brouillon ; il devient obligatoire au moment d'envoyer/accepter. Rendre l'erreur ET
  // scroller dessus : un champ obligatoire muet rend le bouton "sans effet" (piege connu du repo).
  const guardOffreTechnique = useCallback((): boolean => {
    if (offreTechnique.trim().length === 0) {
      setOffreTechniqueError("L'offre technique est obligatoire avant d'envoyer votre offre.");
      window.setTimeout(() => {
        document
          .querySelector('.error, .error-message')
          ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 50);
      return false;
    }
    return true;
  }, [offreTechnique]);

  // Persistance de l'attestation via la route dediee (le back pose/efface attestation_documents_at).
  // POURQUOI persister DES la coche (et pas seulement a l'envoi) : la garde back d'envoi (422) lit la
  // colonne ; en la posant au toggle, elle est deja en base au moment de la signature (chaque point
  // d'envoi passe par une modale de confirmation, la requete a le temps d'aboutir). MAJ optimiste puis
  // rollback si le reseau echoue, pour ne pas laisser croire a une attestation enregistree.
  const handleToggleAttestation = useCallback(
    async (nextChecked: boolean) => {
      setAttesteDocuments(nextChecked);
      if (nextChecked) setAttestationError(null);
      try {
        await saveAttestationDocuments(commande.id, nextChecked);
      } catch (error) {
        setAttesteDocuments(!nextChecked);
        setAttestationError(
          error instanceof Error
            ? error.message
            : "Erreur lors de l'enregistrement de l'attestation.",
        );
      }
    },
    [commande.id],
  );

  // Garde d'obligation A L'ENVOI (miroir front de la garde back 422). Meme forme que
  // guardOffreTechnique : pose l'erreur et scrolle dessus (un champ obligatoire muet rend le bouton
  // "sans effet", piege connu du repo). Les deux gardes coexistent, chacune avec sa propre erreur.
  const guardAttestation = useCallback((): boolean => {
    if (!attesteDocuments) {
      setAttestationError(
        "Vous devez attester que vos documents administratifs sont à jour avant d'envoyer votre offre.",
      );
      window.setTimeout(() => {
        document
          .querySelector('.error, .error-message')
          ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 50);
      return false;
    }
    return true;
  }, [attesteDocuments]);
  // CTA de navigation vers le bon de commande signe : s'efface tant qu'une decision est due ici.
  const nextTabCta = useMemo(
    () => computeOffreNextTabCta(commande, chronoExpired, !!onGoToNotification),
    [commande, chronoExpired, onGoToNotification],
  );

  const op = useMemo(() => commande.operation || {}, [commande.operation]);
  const rang = commande.rang ?? 1;

  // Date/heure limite de depot de l'offre (format "JJ/MM/AAAA à HH:mm") pour le bandeau d'invitation.
  const dateLimiteDepot = commande.expire_at
    ? `${new Date(commande.expire_at).toLocaleDateString('fr-FR')} à ${new Date(
        commande.expire_at,
      ).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`
    : null;

  const subtitle = useMemo(() => {
    const lt = op.lot_technique;
    const dep = op.lot_departement?.departement;
    return lt && dep ? `${lt.id} - ${lt.nom}, ${dep.id} - ${dep.nom}` : 'Informations de lot non disponibles';
  }, [op]);

  // `dateTimeFr` retire : l'horodatage des messages de refus vit desormais dans le domaine
  // (messageRefusPrestataire), partage avec l'onglet Consultation.

  // --- ACCEPT (panier normal) : CGV puis signature ---------------------------
  const openAcceptSignature = useCallback(() => {
    setPendingSign({
      mode: 'accept',
      signerUrl: buildAcceptSignerUrl(commande.id),
      successTitle: 'Opération acceptée',
      successBody: "Votre signature a été apposée. L'acheteur a été informé par email.",
    });
  }, [commande.id]);

  const handleAccepterAvecCGV = useCallback(() => {
    // Les CGV passent TOUJOURS avant la signature ACCEPT ; on ne saute la popup que si elles
    // ont deja ete cochees dans cette session (un refresh les redemande donc).
    if (acceptCGV) {
      openAcceptSignature();
    } else {
      setReadyToAcceptAfterCGV(true);
      setIsCGVModalOpen(true);
    }
  }, [acceptCGV, openAcceptSignature]);

  // --- TRANSFER (panier inverse) : envoyer l'offre, signature SANS CGV -------
  const openTransferSignature = useCallback(() => {
    setPendingSign({
      mode: 'transfer',
      signerUrl: buildTransferSignerUrl(commande.id),
      successTitle: 'Offre envoyée',
      successBody: 'Votre signature a été apposée et votre offre envoyée à l’acheteur.',
    });
  }, [commande.id]);

  // --- REFUS (panier normal ET inverse, meme route, NON signe) ---------------
  const handleConfirmRefus = useCallback(async () => {
    setInlineError(null);
    setIsRefusing(true);
    try {
      await refuserCommande(commande.id); // id-based, jamais window.location
      setRefuseSuccess(true);
    } catch (error) {
      setInlineError(error instanceof Error ? error.message : "Erreur lors du refus de l'opération");
      setIsRefuseModalOpen(false);
    } finally {
      setIsRefusing(false);
    }
  }, [commande.id]);

  const handleCloseRefusSuccess = useCallback(async () => {
    setIsRefuseModalOpen(false);
    setRefuseSuccess(false);
    await refetch(); // refetch via prop, jamais window.location.reload()
  }, [refetch]);

  // --- Catalogue (panier inverse) --------------------------------------------
  const handleAddToCommand = useCallback((articles: Article[]) => {
    setExistingArticles(articles);
    setIsArticlesModalOpen(false);
  }, []);

  const handleAskDelete = useCallback((catalogueId: string) => {
    setInlineError(null);
    setPendingDeleteId(catalogueId);
    setIsDeleteModalOpen(true);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    const catalogueId = pendingDeleteId;
    setIsDeleteModalOpen(false);
    if (!catalogueId) return;
    setDeletingIds((prev) => new Set(prev).add(catalogueId));
    try {
      await deleteCatalogueArticle(commande.id, catalogueId);
      setExistingArticles((prev) =>
        prev.filter((a) => a.catalogueId !== catalogueId && a.id !== catalogueId),
      );
    } catch (error) {
      setInlineError(
        error instanceof Error ? error.message : "Erreur lors de la suppression de l'article",
      );
    } finally {
      setDeletingIds((prev) => {
        const next = new Set(prev);
        next.delete(catalogueId);
        return next;
      });
      setPendingDeleteId(null);
    }
  }, [pendingDeleteId, commande.id]);

  // --- DOWNLOAD bon de commande signe (Valide 7) -----------------------------
  // --- onSigned : refetch (+ bascule onglet propre pour le transfer) ---------
  const handleSigned = useCallback(async () => {
    const mode = pendingSign?.mode;
    setPendingSign(null);
    await refetch();
    // Destination DECLAREE, pas une bascule immediate : `refetch` vient de changer le statut, mais
    // les descripteurs de cette closure sont encore ceux d'avant. Un `goToTab` verrait "Notification"
    // ferme alors que l'acceptation vient de l'ouvrir. Le resolveur tranche au render suivant.
    // accept   -> Notification et signature (repli Offre si la contre-signature manque encore).
    // transfer -> Offre : l'offre part chez l'acheteur, on reste dessus en read-only.
    if (!requestTab) return;
    requestTab(mode === 'transfer' ? ['offre'] : ['notification', 'offre']);
  }, [pendingSign, refetch, requestTab]);

  // Une rangee d'action porte son propre PrintButton (panier normal + A chiffrer) : on n'ajoute
  // le PrintButton global en bas que pour les autres etats (legacy : PrintButton dans chaque
  // rangee d'action, OffreBC l.1911-1924 / 1964-1977).
  const actionRowVisible = showAccepterRefuser || isChiffrage;

  const printSuiviHeaderProps = {
    titre: op.nom || 'Commande BC',
    sousTitre: '',
    subtitle,
    status: commande.status?.nom || 'Statut inconnu',
    statusColor: getStatusColor(commande.status?.nom || ''),
    delais: commande.expire_at ?? undefined,
    backgroundImage: lotImage,
  };


  // Banniere orange « chiffrage demande » (presta) : meme banniere en HAUT de page et dans la
  // section Descriptif financier (remplace l'ancien bandeau bleu d'instructions).
  const chiffrageBanner = (
    <Banner
      variant="warning"
      message="L'acheteur vous demande de définir les articles nécessaires pour cette opération. Sélectionnez les articles appropriés dans le catalogue."
    />
  );

  // Invitation a deposer l'offre avant l'echeance : presta, quel que soit le mode (panier normal
  // ou inverse a chiffrer), tant que l'action est encore possible. Rendue en HAUT et en BAS (avant
  // les boutons d'action).
  const depotBanner =
    (showAccepterRefuser || isChiffrage) && dateLimiteDepot ? (
      <Banner
        variant="warning"
        message={`Vous êtes invités à déposer votre offre jusqu'au ${dateLimiteDepot}`}
      />
    ) : null;

  // Date/heure du dernier changement de statut (refus / acceptation), pour les bannieres.
  const statusDate = commande.updated_at ? new Date(commande.updated_at) : null;
  const statusDateStr =
    statusDate && !Number.isNaN(statusDate.getTime())
      ? `${statusDate.toLocaleDateString('fr-FR')} à ${statusDate.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`
      : '';

  // Bannieres d'ETAT (refus / expiration / annulation / acceptation), definies UNE fois et rendues en
  // HAUT ET EN BAS de l'onglet — meme dispositif que "Notification et signature". `danger` pour un
  // refus : il ferme le parcours, la ou `warning` le rangeait au niveau d'un rappel d'echeance.
  // Le message vient du domaine (messageRefusPrestataire), partage avec l'onglet Consultation qui
  // affiche volontairement le meme etat : deux textes recopies finiraient par se contredire.
  const stateBanners = (
    <>
      {offreState.showRefusMessage && offreState.refusInfo && (
        <Banner variant="danger" filled message={messageRefusPrestataire(offreState.refusInfo)} />
      )}
      {statusBanner.visible && statusBanner.variant === 'accepted' && (
        <Banner
          variant="success"
          filled
          message={`Bon de commande accepté et signé${statusDateStr ? ` le ${statusDateStr}` : ''}`}
        />
      )}
    </>
  );

  return (
    <div className="consultation-bc" ref={printRef}>
      {depotBanner}
      {isChiffrage && chiffrageBanner}
      {/* Bandeau expiration / annulation (legacy OffreBC l.1486-1501) */}
      {expired && (
        <div className="cmd-offre__expired-notice">
          {commande.status?.id === 14
            ? "Cette commande a été annulée par l'acheteur. Vous ne pouvez plus effectuer d'actions."
            : 'Cette commande est expirée. Vous ne pouvez plus modifier votre offre ni effectuer d’actions.'}
        </div>
      )}

      {stateBanners}
      {statusBanner.visible && statusBanner.variant !== 'refused' && statusBanner.variant !== 'accepted' && (
        <div className={`cmd-offre__status cmd-offre__status--${statusBanner.variant}`}>
          {statusBanner.message}
        </div>
      )}

      {/* Preparation du BC, EN TETE de l'onglet — parite avec la vue acheteur. `titulaire={commande}` :
          le presta EST le titulaire, sa commande porte son contrat. */}
      <OperationPreparationBC operation={op} titulaire={commande} />

      {/* Descriptif Technique : miroir EXACT de l'onglet Consultation BC (bandeau info + 4 champs).
          Le Descriptif Administratif n'est PAS repris ici : il vit dans l'onglet Consultation. */}
      {/* Offre technique : le presta SAISIT la sienne. Remplace le copier-coller du descriptif
          technique de l'acheteur (qui reste consultable dans l'onglet Consultation). Le texte est
          contractuel ; obligatoire a l'envoi, fige des l'envoi (canEditOffreTechnique). */}
      <SectionPliable title="Offre technique" defaultOpen>
        <Banner
          variant="info"
          message="L'offre technique que vous saisissez ci-dessous est contractuelle. Toute précision administrative ou juridique de nature à contrevenir à l'accord-cadre général, sans dérogation prévue au contrat, est considérée comme inexistante."
        />
        <div className="section-content">
          <div className="form-group">
            <label>
              Offre technique {canEditOffreTechnique && <span className="required">*</span>}
            </label>
            {canEditOffreTechnique ? (
              <>
                <textarea
                  value={offreTechnique}
                  maxLength={OFFRE_TECHNIQUE_MAX}
                  rows={8}
                  placeholder="Décrivez votre offre technique."
                  className={offreTechniqueError ? 'error' : ''}
                  onChange={(e) => {
                    setOffreTechnique(e.target.value);
                    if (offreTechniqueError) setOffreTechniqueError(null);
                  }}
                  onBlur={handleSaveOffreTechnique}
                />
                <div style={{ textAlign: 'right', fontSize: '1.2rem', color: '#6b7280' }}>
                  {offreTechnique.length}/{OFFRE_TECHNIQUE_MAX}
                </div>
                {offreTechniqueError && (
                  <span className="error-message">{offreTechniqueError}</span>
                )}
              </>
            ) : (
              // Fige : apres envoi/acceptation, l'offre technique se lit comme le reste de l'offre.
              <div className="input-inactif bcc-desc__besoin-readonly">{offreTechnique || '-'}</div>
            )}
          </div>
        </div>
      </SectionPliable>

      {/* Offre financiere : le coeur, varie selon panier / etat */}
      <SectionPliable title="Offre financière" defaultOpen>
        <div className="section-content">
          {isChiffrage ? (
            // A) PANIER INVERSE A CHIFFRER (transferred + AChiffrer(11), non expire)
            <div className="cmd-offre__chiffrage">
              {chiffrageBanner}

              {existingArticles.length > 0 ? (
                <>
                  <div className="articles-table-container">
                    <div className="articles-table-wrapper">
                      <table>
                        <thead>
                          <tr>
                            <th>N° Réf</th>
                            <th>Description de la prestation</th>
                            <th>Unité</th>
                            <th>Quantité</th>
                            <th>Prix unitaire HT</th>
                            <th>Total HT</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {existingArticles.map((article) => {
                            const key = article.catalogueId || article.id;
                            return (
                              <tr key={key}>
                                <td className="article-number">{article.code}</td>
                                <td className="article-description">
                                  <div className="article-name">{article.nom}</div>
                                </td>
                                <td className="article-unit">{article.unite}</td>
                                <td className="article-quantity">
                                  <div className="quantity-cell">{article.quantite}</div>
                                </td>
                                <td className="article-price price-cell">
                                  {formatPrice(
                                    ArticlesService.getPrixByQuantity(article, article.quantite),
                                    4,
                                    true,
                                  )}
                                </td>
                                <td className="article-total total-cell">
                                  {formatPrice(article.totalHT, 4, true)}
                                </td>
                                <td className="article-actions">
                                  <button
                                    type="button"
                                    className="delete-button"
                                    onClick={() => handleAskDelete(key)}
                                    disabled={deletingIds.has(key)}
                                    aria-label="Supprimer l'article"
                                  >
                                    {deletingIds.has(key) ? (
                                      <span className="delete-spinner" aria-label="Suppression en cours" />
                                    ) : (
                                      '×'
                                    )}
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                      <button
                        className="add-article-button"
                        type="button"
                        onClick={() => setIsArticlesModalOpen(true)}
                      >
                        Ajouter ou modifier un article
                      </button>
                    </div>

                    <div className="articles-actions">
                      <div className="total-section">
                        <p>Total HT de votre offre : {formatPrice(totalChiffrage, 4, true)}</p>
                      </div>
                    </div>
                  </div>

                  <div className="cmd-offre__chiffrage-send">
                    <Button
                      value="Envoyer l'offre à l'acheteur"
                      variant="primary"
                      size="medium"
                      onClick={() => {
                        // Les deux gardes coexistent (offre technique + attestation), chacune avec
                        // son erreur ; le && applique la meme condition qu'attendu par la spec.
                        if (guardOffreTechnique() && guardAttestation()) setIsEnvoyerModalOpen(true);
                      }}
                      disabled={existingArticles.length === 0}
                    />
                  </div>
                </>
              ) : (
                // Apercu du catalogue rendu DIRECTEMENT dans la page (comme cote acheteur) : un clic
                // n'importe ou ouvre la modale de composition du bon de commande. La preview reste
                // montee tant qu'aucun article n'est ajoute (donc visible pendant que la modale est ouverte).
                // Drive par la COMMANDE (contrat) -> catalogue du RANG CONSULTE, comme la modale.
                <CataloguePreview
                  commande={commande}
                  lotNom={op.lot_technique?.nom}
                  onOpen={() => setIsArticlesModalOpen(true)}
                />
              )}
            </div>
          ) : readOnlyArticles.length > 0 ? (
            // B) OFFRE ENVOYEE (AttenteAnalyse 13) + C) PANIER NORMAL / autres : read-only.
            <div className="articles-table-container">
              {statusFlags.isWaiting && (
                <div className="cmd-offre__info">
                  Votre offre a été envoyée à l'acheteur. Vous serez informé de sa décision.
                </div>
              )}
              <div className="articles-table-wrapper">
                <table>
                  <thead>
                    <tr>
                      <th>N° Réf</th>
                      <th>Description de la prestation</th>
                      <th>Unité</th>
                      <th>Quantité</th>
                      <th>Prix unitaire HT</th>
                      <th>Total HT</th>
                    </tr>
                  </thead>
                  <tbody>
                    {readOnlyArticles.map((article) => (
                      <tr key={article.catalogueId || article.id}>
                        <td>{article.code}</td>
                        <td>
                          <div className="article-name">{article.nom}</div>
                        </td>
                        <td>{article.unite}</td>
                        <td>{article.quantite}</td>
                        <td>{formatPrice(article.prix, 4, true)}</td>
                        <td>{formatPrice(article.totalHT, 4, true)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="articles-actions">
                <div className="total-section">
                  <p>
                    <strong>
                      Total de l'offre (Rang {rang}) : {formatPrice(totalReadOnly, 4, true)} HT
                    </strong>
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="no-articles-message">
              <p>Aucun article défini pour cette offre.</p>
            </div>
          )}
        </div>
      </SectionPliable>

      {/* Le bon de commande signe (preview + telechargement) vit desormais dans l'onglet
          "Notification et signature" (actif au statut Valide=7). */}

      {/* Rappel de l'etat juste avant les actions (voir `stateBanners`, defini en tete). */}
      {stateBanners}

      {/* Attestation OBLIGATOIRE "documents administratifs a jour" (regles 1 & 4). Placee AVANT les
          bannieres de rappel et d'erreur : c'est le premier geste attendu du presta avant d'agir.
          Composant partage : il gere lui-meme l'ouverture de la modale de profil sur l'onglet
          Documents ; on ne pilote que l'etat coche + l'erreur d'obligation. entiteId n'est PAS passe :
          le composant lit toujours /profile (profil du presta connecte). La case reste montee et
          interactive apres l'envoi (isWaiting) : regle 6. */}
      {showAttestation && (
        <AttestationDocuments
          checked={attesteDocuments}
          onChange={handleToggleAttestation}
          error={attestationError}
        />
      )}

      {inlineError && (
        <div className="cmd-offre__inline-message cmd-offre__inline-message--error">{inlineError}</div>
      )}

      {/* Rappel de l'echeance juste avant les boutons d'action (meme bandeau qu'en haut). */}
      {depotBanner}

      {/* Rangee d'actions presta */}
      <div className="cmd-offre__actions">
        {/* PANIER NORMAL : accepter / refuser direct (legacy l.1902-1933) */}
        {showAccepterRefuser && !hasActed && (
          <>
            <Button
              value="Refuser l'opération"
              variant="cancel"
              onClick={() => {
                setInlineError(null);
                setRefuseSuccess(false);
                setIsRefusing(false);
                setIsRefuseModalOpen(true);
              }}
            />
            <PrintButton
              targetRef={printRef}
              fileName={`${op.numero || commande.id} - Offre`}
              value="Imprimer l'offre"
              suiviHeaderProps={printSuiviHeaderProps}
            />
            <Button
              value="Accepter l'opération"
              variant="primary"
              onClick={() => {
                // Attestation obligatoire (regle 4) ajoutee dans la MEME condition que la garde
                // offre technique deja en place ; chaque garde porte sa propre erreur.
                if (guardOffreTechnique() && guardAttestation()) handleAccepterAvecCGV();
              }}
            />
          </>
        )}

        {/* PANIER INVERSE : refuser de faire une offre / envoyer (legacy l.1935-1990) */}
        {isChiffrage && (
          <>
            <Button
              value="Refuser de faire une offre"
              variant="cancel"
              onClick={() => {
                setInlineError(null);
                setRefuseSuccess(false);
                setIsRefusing(false);
                setIsRefuseModalOpen(true);
              }}
            />
            <PrintButton
              targetRef={printRef}
              fileName={`${op.numero || commande.id} - Offre`}
              value="Imprimer l'offre"
              suiviHeaderProps={printSuiviHeaderProps}
            />
            <Button
              value="Envoyer mon offre à l'acheteur"
              variant="primary"
              onClick={() => {
                // Ce bouton declenche le MEME envoi (transfert) que "Envoyer l'offre a l'acheteur"
                // rendu au-dessus : on lui applique les MEMES gardes (offre technique + attestation)
                // pour que l'attestation soit obligatoire quel que soit le point d'envoi (regle 4).
                if (guardOffreTechnique() && guardAttestation()) setIsEnvoyerModalOpen(true);
              }}
              disabled={existingArticles.length === 0}
            />
          </>
        )}

        {/* Etats sans rangee d'action : PrintButton global (sinon une ligne le porte deja) */}
        {!actionRowVisible && (
          <PrintButton
            targetRef={printRef}
            fileName={`${op.numero || commande.id} - Offre`}
            value="Imprimer l'offre"
            suiviHeaderProps={printSuiviHeaderProps}
          />
        )}

        {/* Le bon de commande signe vit dans l'onglet Notification : une fois l'offre acceptee, la
            suite du parcours est la-bas. En dernier : l'orange ferme la rangee, comme les actions. */}
        <NextTabButton
          label={nextTabCta.label}
          hidden={nextTabCta.hidden}
          // `no-print` : de la navigation n'a rien a faire sur un document imprime, et `printRef`
          // couvre tout l'onglet (barre d'actions comprise).
          className="no-print"
          onClick={() => onGoToNotification?.()}
        />
      </div>

      {/* --- Modales ----------------------------------------------------------- */}

      {/* CGV avant ACCEPT uniquement */}
      <CGVAccept
        acceptCGV={acceptCGV}
        setAcceptCGV={setAcceptCGV}
        isOpen={isCGVModalOpen}
        onClose={() => {
          setIsCGVModalOpen(false);
          setReadyToAcceptAfterCGV(false);
        }}
        onConfirm={() => {
          setAcceptCGV(true);
          setTimeout(() => {
            setIsCGVModalOpen(false);
            if (readyToAcceptAfterCGV) {
              setReadyToAcceptAfterCGV(false);
              openAcceptSignature();
            }
          }, 100);
        }}
      />

      {/* Selection des articles du catalogue (panier inverse) */}
      <ModalArticles
        isOpen={isArticlesModalOpen}
        onClose={() => setIsArticlesModalOpen(false)}
        onAddToCommand={handleAddToCommand}
        commandes={[commande]}
        existingSelection={existingArticles}
        lotNom={op.lot_technique?.nom || 'Lot technique'}
        prestataireRang={rang}
      />

      {/* Confirmation envoi de l'offre (transfer) -> signature SANS CGV */}
      <ValidationPopUp
        isOpen={isEnvoyerModalOpen}
        title="Envoyer votre offre à l'acheteur"
        text={`Êtes-vous sûr de vouloir envoyer votre offre d'un montant de ${formatPrice(
          totalChiffrage,
          4,
          true,
        )} HT à l'acheteur pour validation ? L'acheteur devra ensuite valider votre proposition.`}
        onClose={() => setIsEnvoyerModalOpen(false)}
        onConfirm={() => {
          setIsEnvoyerModalOpen(false);
          openTransferSignature();
        }}
        cancelLabel="Annuler"
        confirmLabel="Envoyer l'offre"
      />

      {/* Confirmation de refus -> succes -> refetch (pas de reload) */}
      <ValidationPopUp
        isOpen={isRefuseModalOpen}
        title={refuseSuccess ? 'Opération refusée' : "Refuser l'opération"}
        text={
          refuseSuccess
            ? 'Opération refusée avec succès.'
            : 'Êtes-vous sûr de vouloir refuser cette opération ? Cette action est irréversible.'
        }
        isLoading={isRefusing}
        onClose={refuseSuccess ? handleCloseRefusSuccess : () => setIsRefuseModalOpen(false)}
        onConfirm={refuseSuccess ? handleCloseRefusSuccess : handleConfirmRefus}
        cancelLabel={refuseSuccess ? undefined : 'Annuler'}
        confirmLabel={refuseSuccess ? 'Fermer' : 'Oui, refuser'}
      />

      {/* Confirmation de suppression d'un article (remplace confirm() natif) */}
      <ValidationPopUp
        isOpen={isDeleteModalOpen}
        title="Supprimer l'article"
        text="Êtes-vous sûr de vouloir supprimer cet article ?"
        onClose={() => {
          setIsDeleteModalOpen(false);
          setPendingDeleteId(null);
        }}
        onConfirm={handleConfirmDelete}
        cancelLabel="Annuler"
        confirmLabel="Oui, supprimer"
      />

      {/* Flux signature presta : accept (panier normal) ou transfer (panier inverse).
          operationId === commandeId === commande.id ; requireOtp NON passe -> SignatureFlow
          le deduit false pour le BDC (signature manuscrite nom + dessin, SANS OTP). */}
      {pendingSign && (
        <SignatureFlow
          operationId={commande.id}
          commandeId={commande.id}
          resourcePrefix="bdc/prestataire"
          signerUrl={pendingSign.signerUrl}
          successTitle={pendingSign.successTitle}
          successBody={pendingSign.successBody}
          onClose={() => setPendingSign(null)}
          onSigned={handleSigned}
        />
      )}
    </div>
  );
};

export default CommandeOffre;
